import React, { useState, useCallback, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { CourseProvider, useCourse } from "./context/CourseContext";
import { NavigationProvider } from "./context/NavigationContext";
import Navbar from "./components/Navbar";

import Login from "./components/Login";
import Signup from "./components/Signup";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PricingPlans from "./components/pricing/PricingPlans";

import Dashboard from "./components/Dashboard";
import DeckList from "./components/srs/DeckList";
import CardList from "./components/srs/CardList";
import LearningManager from "./components/srs/LearningManager";
import MultiStepCardLearningManager from "./components/srs/MultiStepLearningManager";
import ReviewContainer from "./components/srs/ReviewMode/ReviewContainer";
import VisitingPage from "./components/srs/VisitingPage";
import AddCard from "./components/srs/AddCard";
import MultiStepCard from "./components/srs/MultiStepCard";
import MultiStepCardViewer from "./components/srs/MultiCardStepViewer";

import { AdminDashboard } from "./components/language_learning/admin_console/admin_index";

import { getToken, getUsername, logout } from "./utils/auth";
import LanguageLearningApp from "./components/language_learning/user_console/LanguageLearningApp";
import ViewCourseContent from "./components/language_learning/user_console/ViewCourseContent";
import CourseletSentencesView from "./components/language_learning/user_console/CourseletSentencesView";
import HomePage from "./components/Homepage";
import LanguageSelector from "./components/language_learning/user_console/LanguageSelector";
import GameWrapper from "./experiment/GameWrapper";
import SpeechRecognizer from "./experiment/SpeechRecognizer";
import EmbeddableSquirrelMascot from './experiment/Squirrel'

import VocabularyGameDemo from "./components/pictionary/VocabGameDemo";
import GamesPage from "./components/GamesPage";
import ReviewModeAdapter from "./components/srs/ReviewMode/ReviewModeAdapter";
import Conversation from "./components/conversation/Conversation";

import GoalSetting from "./components/GoalSetting";

import ExampleComponent from "./ExampleComponent";
import FlashcardProgressCard from "./components/language_learning/user_console/FlashcardProgressCard";
import { TourProvider } from "./components/tour/TourContext";

const App = () => {
    const [token, setToken] = useState(getToken());
    const [username, setUsername] = useState(getUsername() || "");

    return (
        <Router>
            <CourseProvider>
                <TourProvider>
                    <NavigationProvider>
                        <AppContent
                            token={token}
                            setToken={setToken}
                            username={username}
                            setUsername={setUsername}
                        />
                    </NavigationProvider>
                </TourProvider>
            </CourseProvider>
        </Router>
    );
};

const AppContent = ({ token, setToken, username, setUsername }) => {
    const navigate = useNavigate();
    const { currentCourse } = useCourse();

    // FIXED: Memoize the logout handler to prevent unnecessary re-renders
    const handleLogout = useCallback(() => {
        logout();
        setToken(null);
        setUsername("");
        navigate('/');
    }, [setToken, setUsername, navigate]);

    // FIXED: Memoize the default language function to prevent unnecessary re-renders
    const getDefaultLanguage = useMemo(() => {
        if (currentCourse && currentCourse.language) {
            return currentCourse.language;
        }
        return "en"; // Default to English if no course is selected
    }, [currentCourse]);

    return (
        <>
            <Navbar
                token={token}
                username={username}
                handleLogout={handleLogout}
                navigate={navigate}
                course={currentCourse}
            />

            <Routes>
                <Route path="/set-daily-goal" element={<GoalSetting token={token} />} />
                <Route path="/upgrade" element={<PricingPlans />} />

                <Route path="/" element={<VisitingPage />} />
                <Route path="/vocab-game" element={token ? <VocabularyGameDemo /> : <Navigate to="/login" />} />

                <Route path="/game" element={token ? <GameWrapper /> : <Navigate to="/login" />} />
                <Route path="/games" element={token ? <GamesPage /> : <Navigate to="/login" />} />
                <Route path="/flashcard-progress" element={token ? <FlashcardProgressCard token={token} /> : <Navigate to="/login" />} />

                <Route path="/squirrel" element={token ? <EmbeddableSquirrelMascot /> : <Navigate to="/login" />} />

                <Route path="/speech-recognition" element={token ? <SpeechRecognizer /> : <Navigate to="/login" />} />

                <Route path="/welcome" element={<HomePage />} />

                <Route path="/example" element={<ExampleComponent />} />

                {/* Chat routes - general and language-specific */}
                <Route path="/chat" element={token ? <Navigate to={`/chat/${getDefaultLanguage}`} /> : <Navigate to="/login" />} />
                <Route path="/chat/:language" element={token ? <Conversation token={token} /> : <Navigate to="/login" />} />

                <Route path="/login" element={<Login setToken={setToken} setUsername={setUsername} navigate={navigate} />} />
                <Route path="/signup" element={<Signup navigate={navigate} />} />

                <Route path="/forgot-password" element={<ForgotPassword navbar={<Navbar token={token} setToken={setToken} username={username} setUsername={setUsername} />} />} />
                <Route path="/reset-password/:token" element={<ResetPassword navbar={<Navbar token={token} setToken={setToken} username={username} setUsername={setUsername} />} />} />

                <Route path="/learn/:deckId" element={token ? <LearningManager token={token} /> : <Navigate to="/login" />} />
                <Route path="/course-learn" element={token ? <MultiStepCardLearningManager token={token} courseMode={true} /> : <Navigate to="/login" />} />
                <Route path="/multi-step-learn/:deckId" element={token ? <MultiStepCardLearningManager token={token} courseMode={false} /> : <Navigate to="/login" />} />

                <Route path="/review/:deckId" element={token ? <ReviewContainer token={token} /> : <Navigate to="/login" />} />

                <Route path="/review-adapter/:deckId" element={token ? <ReviewModeAdapter token={token} /> : <Navigate to="/login" />} />

                <Route path="/dashboard" element={token ? <Dashboard token={token} /> : <Navigate to="/login" />} />
                <Route path="/decks" element={token ? <DeckList token={token} /> : <Navigate to="/login" />} />
                <Route path="/decks/:deckId" element={<CardList token={token} />} />
                {/* Todo: Create wrapper container on card containers that can invoke the 
                appropriate card components based on deckTy[e*/}

                <Route path="/decks/:deckId/add-card" element={token ? <AddCard token={token} /> : <Navigate to="/login" />} />
                <Route path="/decks/:deckId/add-multi-step-card" element={token ? <MultiStepCard token={token} /> : <Navigate to="/login" />} />
                {/* <Route path="/test/:deckId" element={token ? <MultiStepCard token={token} /> : <Navigate to = "/login"/>} /> */}
                <Route path="/multi-step-cards/:cardId" element={token ? <MultiStepCardViewer /> : <Navigate to="/login" />} />

                {/** Language learning components begin */}
                {/* Admin Console */}
                <Route path="/admin-dashboard" element={token ? <AdminDashboard /> : <Navigate to="/login" />} />

                {/** User console */}
                <Route path="/view-course/:courseId" element={token ? <ViewCourseContent token={token} /> : <Navigate to="/login" />} />
                <Route path="/language-dashboard/:courseId" element={token ? <LanguageLearningApp token={token} /> : <Navigate to="/login" />} />
                <Route path="/view-courselet/:courseletId" element={token ? <CourseletSentencesView /> : <Navigate to="/login" />} />

                <Route path="/languages" element={token ? <LanguageSelector tokenFromApp={token} /> : <Navigate to="/login" />} />

            </Routes>
        </>
    );
};

export default App;